import type { CoursePack, Point } from "./messages";

export type SegmentObstacle = {
  kind: "rail";
  x: number;
  y: number;
  w: number;
  h: number;
  angle?: number;
};

export type MovingRailObstacle = {
  kind: "movingRail";
  x: number;
  y: number;
  w: number;
  h: number;
  angle?: number;
  axis: "x" | "y";
  distance: number;
  period: number;
  phase?: number;
};

export type DiscZone = {
  kind: "fan" | "sand" | "rotatingBunker" | "jump" | "bumper" | "portal";
  x: number;
  y: number;
  r: number;
  angle?: number;
  force?: number;
  boost?: number;
  strength?: number;
  orbitRadius?: number;
  period?: number;
  phase?: number;
  count?: number;
  group?: string;
  exit?: number;
};

export type TriangleObstacle = {
  kind: "triangle";
  points: [Point, Point, Point];
};

export type CourseObstacle = SegmentObstacle | MovingRailObstacle | TriangleObstacle;

export type HoleDefinition = {
  id: number;
  familyId?: number;
  name: string;
  par: number;
  size: { w: number; h: number };
  start: Point;
  cup: Point;
  fairway: Point[];
  obstacles: CourseObstacle[];
  zones: DiscZone[];
};

const HANDMADE_HOLES: HoleDefinition[] = [
  {
    id: 1,
    name: "Opening Bend",
    par: 3,
    size: { w: 920, h: 680 },
    start: { x: 185, y: 530 },
    cup: { x: 718, y: 170 },
    fairway: [
      { x: 100, y: 590 },
      { x: 100, y: 390 },
      { x: 315, y: 390 },
      { x: 315, y: 245 },
      { x: 560, y: 245 },
      { x: 560, y: 95 },
      { x: 815, y: 95 },
      { x: 815, y: 260 },
      { x: 695, y: 372 },
      { x: 500, y: 372 },
      { x: 500, y: 590 },
    ],
    obstacles: [
      { kind: "rail", x: 248, y: 478, w: 120, h: 18, angle: 0.18 },
      {
        kind: "triangle",
        points: [
          { x: 632, y: 276 },
          { x: 704, y: 306 },
          { x: 632, y: 344 },
        ],
      },
    ],
    zones: [
      { kind: "fan", x: 398, y: 332, r: 43, angle: -0.28, force: 0.00042 },
      { kind: "sand", x: 616, y: 492, r: 48 },
    ],
  },
  {
    id: 2,
    name: "Switchback",
    par: 4,
    size: { w: 940, h: 700 },
    start: { x: 165, y: 585 },
    cup: { x: 735, y: 188 },
    fairway: [
      { x: 90, y: 630 },
      { x: 90, y: 500 },
      { x: 680, y: 500 },
      { x: 680, y: 430 },
      { x: 220, y: 430 },
      { x: 220, y: 135 },
      { x: 835, y: 135 },
      { x: 835, y: 265 },
      { x: 360, y: 265 },
      { x: 360, y: 300 },
      { x: 820, y: 300 },
      { x: 820, y: 630 },
    ],
    obstacles: [
      { kind: "rail", x: 526, y: 464, w: 150, h: 18, angle: -0.42 },
      { kind: "rail", x: 505, y: 218, w: 135, h: 18, angle: 0.28 },
    ],
    zones: [
      { kind: "fan", x: 720, y: 360, r: 46, angle: Math.PI, force: 0.0004 },
      { kind: "sand", x: 272, y: 555, r: 44 },
    ],
  },
  {
    id: 3,
    name: "Flag Alley",
    par: 3,
    size: { w: 900, h: 690 },
    start: { x: 185, y: 540 },
    cup: { x: 690, y: 145 },
    fairway: [
      { x: 125, y: 610 },
      { x: 125, y: 410 },
      { x: 355, y: 410 },
      { x: 355, y: 315 },
      { x: 255, y: 315 },
      { x: 255, y: 110 },
      { x: 775, y: 110 },
      { x: 775, y: 235 },
      { x: 605, y: 235 },
      { x: 605, y: 610 },
    ],
    obstacles: [
      { kind: "rail", x: 500, y: 265, w: 170, h: 18, angle: 0 },
      {
        kind: "triangle",
        points: [
          { x: 450, y: 420 },
          { x: 545, y: 468 },
          { x: 455, y: 520 },
        ],
      },
    ],
    zones: [
      { kind: "fan", x: 385, y: 205, r: 47, angle: Math.PI / 2, force: 0.00042 },
      { kind: "fan", x: 520, y: 560, r: 42, angle: -Math.PI / 2, force: 0.00038 },
    ],
  },
  {
    id: 4,
    name: "Twin Fans",
    par: 4,
    size: { w: 960, h: 700 },
    start: { x: 210, y: 570 },
    cup: { x: 748, y: 184 },
    fairway: [
      { x: 120, y: 615 },
      { x: 120, y: 390 },
      { x: 245, y: 390 },
      { x: 245, y: 245 },
      { x: 475, y: 245 },
      { x: 475, y: 105 },
      { x: 835, y: 105 },
      { x: 835, y: 330 },
      { x: 700, y: 330 },
      { x: 700, y: 490 },
      { x: 575, y: 615 },
    ],
    obstacles: [
      { kind: "rail", x: 358, y: 390, w: 145, h: 18, angle: -0.35 },
      { kind: "rail", x: 616, y: 284, w: 120, h: 18, angle: 0.65 },
    ],
    zones: [
      { kind: "fan", x: 285, y: 505, r: 46, angle: 0, force: 0.00045 },
      { kind: "fan", x: 575, y: 178, r: 45, angle: Math.PI / 2, force: 0.00045 },
      { kind: "sand", x: 750, y: 415, r: 45 },
    ],
  },
  {
    id: 5,
    name: "Slanted Box",
    par: 4,
    size: { w: 940, h: 690 },
    start: { x: 185, y: 520 },
    cup: { x: 730, y: 150 },
    fairway: [
      { x: 105, y: 595 },
      { x: 105, y: 285 },
      { x: 275, y: 285 },
      { x: 275, y: 105 },
      { x: 825, y: 105 },
      { x: 825, y: 235 },
      { x: 710, y: 345 },
      { x: 710, y: 590 },
      { x: 480, y: 590 },
      { x: 480, y: 430 },
      { x: 330, y: 430 },
      { x: 330, y: 595 },
    ],
    obstacles: [
      {
        kind: "triangle",
        points: [
          { x: 535, y: 260 },
          { x: 645, y: 305 },
          { x: 535, y: 360 },
        ],
      },
      { kind: "rail", x: 245, y: 382, w: 155, h: 18, angle: 0.3 },
    ],
    zones: [
      { kind: "sand", x: 405, y: 195, r: 50 },
      { kind: "fan", x: 590, y: 506, r: 45, angle: -0.8, force: 0.00048 },
    ],
  },
  {
    id: 6,
    name: "Narrow Mouth",
    par: 3,
    size: { w: 900, h: 670 },
    start: { x: 190, y: 528 },
    cup: { x: 705, y: 145 },
    fairway: [
      { x: 110, y: 580 },
      { x: 110, y: 380 },
      { x: 300, y: 380 },
      { x: 300, y: 285 },
      { x: 455, y: 285 },
      { x: 455, y: 105 },
      { x: 795, y: 105 },
      { x: 795, y: 230 },
      { x: 650, y: 300 },
      { x: 650, y: 580 },
    ],
    obstacles: [
      { kind: "rail", x: 515, y: 370, w: 170, h: 18, angle: Math.PI / 2 },
      { kind: "rail", x: 575, y: 370, w: 170, h: 18, angle: Math.PI / 2 },
    ],
    zones: [
      { kind: "fan", x: 248, y: 470, r: 44, angle: 0.25, force: 0.00042 },
      { kind: "sand", x: 710, y: 235, r: 43 },
    ],
  },
  {
    id: 7,
    name: "Long Hook",
    par: 5,
    size: { w: 980, h: 720 },
    start: { x: 160, y: 590 },
    cup: { x: 760, y: 160 },
    fairway: [
      { x: 95, y: 635 },
      { x: 95, y: 505 },
      { x: 390, y: 505 },
      { x: 390, y: 410 },
      { x: 180, y: 410 },
      { x: 180, y: 200 },
      { x: 430, y: 200 },
      { x: 430, y: 95 },
      { x: 870, y: 95 },
      { x: 870, y: 295 },
      { x: 690, y: 395 },
      { x: 555, y: 635 },
    ],
    obstacles: [
      { kind: "rail", x: 540, y: 300, w: 185, h: 18, angle: 0.45 },
      {
        kind: "triangle",
        points: [
          { x: 305, y: 280 },
          { x: 385, y: 315 },
          { x: 305, y: 360 },
        ],
      },
    ],
    zones: [
      { kind: "fan", x: 260, y: 545, r: 44, angle: -0.2, force: 0.00044 },
      { kind: "fan", x: 675, y: 222, r: 48, angle: Math.PI / 2, force: 0.00042 },
      { kind: "sand", x: 545, y: 540, r: 52 },
    ],
  },
  {
    id: 8,
    name: "Pocket Lane",
    par: 4,
    size: { w: 930, h: 700 },
    start: { x: 195, y: 560 },
    cup: { x: 712, y: 155 },
    fairway: [
      { x: 110, y: 610 },
      { x: 110, y: 420 },
      { x: 260, y: 420 },
      { x: 260, y: 300 },
      { x: 440, y: 300 },
      { x: 440, y: 110 },
      { x: 805, y: 110 },
      { x: 805, y: 280 },
      { x: 710, y: 280 },
      { x: 710, y: 425 },
      { x: 600, y: 425 },
      { x: 600, y: 610 },
    ],
    obstacles: [
      { kind: "rail", x: 365, y: 505, w: 150, h: 18, angle: -0.2 },
      { kind: "rail", x: 605, y: 325, w: 125, h: 18, angle: -0.62 },
    ],
    zones: [
      { kind: "sand", x: 272, y: 505, r: 45 },
      { kind: "fan", x: 545, y: 210, r: 46, angle: 0.4, force: 0.0004 },
    ],
  },
  {
    id: 9,
    name: "Final Pin",
    par: 5,
    size: { w: 980, h: 720 },
    start: { x: 170, y: 585 },
    cup: { x: 790, y: 155 },
    fairway: [
      { x: 90, y: 635 },
      { x: 90, y: 420 },
      { x: 250, y: 420 },
      { x: 250, y: 305 },
      { x: 150, y: 305 },
      { x: 150, y: 120 },
      { x: 430, y: 120 },
      { x: 430, y: 245 },
      { x: 565, y: 245 },
      { x: 565, y: 95 },
      { x: 875, y: 95 },
      { x: 875, y: 280 },
      { x: 735, y: 395 },
      { x: 610, y: 635 },
    ],
    obstacles: [
      { kind: "rail", x: 350, y: 360, w: 170, h: 18, angle: 0.55 },
      { kind: "rail", x: 620, y: 405, w: 150, h: 18, angle: -0.5 },
      {
        kind: "triangle",
        points: [
          { x: 540, y: 540 },
          { x: 630, y: 585 },
          { x: 540, y: 630 },
        ],
      },
    ],
    zones: [
      { kind: "fan", x: 260, y: 205, r: 47, angle: Math.PI / 2, force: 0.00043 },
      { kind: "fan", x: 715, y: 205, r: 47, angle: Math.PI, force: 0.00043 },
      { kind: "sand", x: 455, y: 493, r: 50 },
    ],
  },
  {
    id: 10,
    name: "Canal Cross",
    par: 4,
    size: { w: 960, h: 700 },
    start: { x: 170, y: 560 },
    cup: { x: 755, y: 155 },
    fairway: [
      { x: 100, y: 610 },
      { x: 100, y: 455 },
      { x: 380, y: 455 },
      { x: 380, y: 335 },
      { x: 250, y: 335 },
      { x: 250, y: 115 },
      { x: 830, y: 115 },
      { x: 830, y: 250 },
      { x: 640, y: 250 },
      { x: 640, y: 375 },
      { x: 805, y: 375 },
      { x: 805, y: 610 },
    ],
    obstacles: [
      { kind: "rail", x: 520, y: 405, w: 175, h: 18, angle: -0.18 },
      { kind: "rail", x: 475, y: 205, w: 135, h: 18, angle: 0.3 },
    ],
    zones: [
      { kind: "sand", x: 245, y: 535, r: 46 },
      { kind: "fan", x: 705, y: 315, r: 44, angle: Math.PI, force: 0.00042 },
    ],
  },
  {
    id: 11,
    name: "Pocket Fan",
    par: 3,
    size: { w: 930, h: 680 },
    start: { x: 180, y: 535 },
    cup: { x: 730, y: 150 },
    fairway: [
      { x: 105, y: 590 },
      { x: 105, y: 395 },
      { x: 300, y: 395 },
      { x: 300, y: 260 },
      { x: 500, y: 260 },
      { x: 500, y: 105 },
      { x: 805, y: 105 },
      { x: 805, y: 265 },
      { x: 690, y: 350 },
      { x: 560, y: 350 },
      { x: 560, y: 590 },
    ],
    obstacles: [
      {
        kind: "triangle",
        points: [
          { x: 620, y: 250 },
          { x: 700, y: 282 },
          { x: 620, y: 322 },
        ],
      },
      { kind: "rail", x: 248, y: 475, w: 128, h: 18, angle: -0.22 },
    ],
    zones: [
      { kind: "fan", x: 410, y: 330, r: 48, angle: -0.45, force: 0.00044 },
      { kind: "sand", x: 700, y: 445, r: 46 },
    ],
  },
  {
    id: 12,
    name: "Bridge Loop",
    par: 4,
    size: { w: 950, h: 705 },
    start: { x: 170, y: 585 },
    cup: { x: 735, y: 178 },
    fairway: [
      { x: 95, y: 635 },
      { x: 95, y: 470 },
      { x: 430, y: 470 },
      { x: 430, y: 375 },
      { x: 235, y: 375 },
      { x: 235, y: 120 },
      { x: 835, y: 120 },
      { x: 835, y: 300 },
      { x: 705, y: 300 },
      { x: 705, y: 430 },
      { x: 575, y: 430 },
      { x: 575, y: 635 },
    ],
    obstacles: [
      { kind: "rail", x: 340, y: 245, w: 135, h: 18, angle: 0.55 },
      { kind: "rail", x: 535, y: 520, w: 140, h: 18, angle: -0.4 },
    ],
    zones: [
      { kind: "sand", x: 280, y: 542, r: 44 },
      { kind: "fan", x: 730, y: 242, r: 45, angle: Math.PI / 2, force: 0.00044 },
    ],
  },
  {
    id: 13,
    name: "Scoop Turn",
    par: 3,
    size: { w: 900, h: 680 },
    start: { x: 190, y: 535 },
    cup: { x: 690, y: 145 },
    fairway: [
      { x: 115, y: 590 },
      { x: 115, y: 365 },
      { x: 360, y: 365 },
      { x: 360, y: 245 },
      { x: 520, y: 245 },
      { x: 520, y: 100 },
      { x: 790, y: 100 },
      { x: 790, y: 250 },
      { x: 660, y: 330 },
      { x: 485, y: 330 },
      { x: 485, y: 590 },
    ],
    obstacles: [
      { kind: "rail", x: 300, y: 455, w: 128, h: 18, angle: 0.35 },
      { kind: "rail", x: 610, y: 282, w: 118, h: 18, angle: -0.55 },
    ],
    zones: [
      { kind: "fan", x: 415, y: 305, r: 43, angle: 0.2, force: 0.00042 },
      { kind: "sand", x: 620, y: 470, r: 48 },
    ],
  },
  {
    id: 14,
    name: "Split Rail",
    par: 4,
    size: { w: 960, h: 705 },
    start: { x: 185, y: 575 },
    cup: { x: 760, y: 160 },
    fairway: [
      { x: 100, y: 625 },
      { x: 100, y: 420 },
      { x: 280, y: 420 },
      { x: 280, y: 295 },
      { x: 430, y: 295 },
      { x: 430, y: 110 },
      { x: 845, y: 110 },
      { x: 845, y: 292 },
      { x: 690, y: 292 },
      { x: 690, y: 455 },
      { x: 555, y: 625 },
    ],
    obstacles: [
      { kind: "rail", x: 525, y: 365, w: 170, h: 18, angle: Math.PI / 2 },
      { kind: "rail", x: 585, y: 365, w: 170, h: 18, angle: Math.PI / 2 },
    ],
    zones: [
      { kind: "fan", x: 255, y: 525, r: 45, angle: -0.15, force: 0.00043 },
      { kind: "sand", x: 742, y: 350, r: 44 },
    ],
  },
  {
    id: 15,
    name: "Wind Shelf",
    par: 5,
    size: { w: 980, h: 720 },
    start: { x: 165, y: 590 },
    cup: { x: 785, y: 160 },
    fairway: [
      { x: 90, y: 640 },
      { x: 90, y: 505 },
      { x: 360, y: 505 },
      { x: 360, y: 392 },
      { x: 195, y: 392 },
      { x: 195, y: 135 },
      { x: 455, y: 135 },
      { x: 455, y: 250 },
      { x: 600, y: 250 },
      { x: 600, y: 95 },
      { x: 875, y: 95 },
      { x: 875, y: 290 },
      { x: 735, y: 405 },
      { x: 600, y: 640 },
    ],
    obstacles: [
      { kind: "rail", x: 350, y: 285, w: 150, h: 18, angle: 0.4 },
      { kind: "rail", x: 640, y: 455, w: 160, h: 18, angle: -0.48 },
    ],
    zones: [
      { kind: "fan", x: 285, y: 205, r: 46, angle: Math.PI / 2, force: 0.00043 },
      { kind: "fan", x: 720, y: 220, r: 48, angle: Math.PI, force: 0.00043 },
      { kind: "sand", x: 500, y: 535, r: 50 },
    ],
  },
  {
    id: 16,
    name: "Tight Ladder",
    par: 4,
    size: { w: 920, h: 700 },
    start: { x: 185, y: 565 },
    cup: { x: 705, y: 160 },
    fairway: [
      { x: 105, y: 615 },
      { x: 105, y: 450 },
      { x: 300, y: 450 },
      { x: 300, y: 345 },
      { x: 430, y: 345 },
      { x: 430, y: 240 },
      { x: 555, y: 240 },
      { x: 555, y: 105 },
      { x: 805, y: 105 },
      { x: 805, y: 275 },
      { x: 665, y: 360 },
      { x: 520, y: 615 },
    ],
    obstacles: [
      { kind: "rail", x: 245, y: 525, w: 115, h: 18, angle: -0.2 },
      { kind: "rail", x: 500, y: 405, w: 130, h: 18, angle: 0.45 },
    ],
    zones: [
      { kind: "sand", x: 365, y: 520, r: 45 },
      { kind: "fan", x: 650, y: 232, r: 45, angle: 0.25, force: 0.00042 },
    ],
  },
  {
    id: 17,
    name: "Backspin Gate",
    par: 4,
    size: { w: 950, h: 700 },
    start: { x: 180, y: 575 },
    cup: { x: 740, y: 170 },
    fairway: [
      { x: 95, y: 625 },
      { x: 95, y: 425 },
      { x: 260, y: 425 },
      { x: 260, y: 285 },
      { x: 450, y: 285 },
      { x: 450, y: 120 },
      { x: 835, y: 120 },
      { x: 835, y: 285 },
      { x: 705, y: 285 },
      { x: 705, y: 425 },
      { x: 590, y: 425 },
      { x: 590, y: 625 },
    ],
    obstacles: [
      { kind: "rail", x: 350, y: 495, w: 150, h: 18, angle: 0.18 },
      { kind: "rail", x: 585, y: 315, w: 125, h: 18, angle: -0.58 },
    ],
    zones: [
      { kind: "fan", x: 530, y: 200, r: 46, angle: -0.35, force: 0.00042 },
      { kind: "sand", x: 280, y: 535, r: 46 },
    ],
  },
  {
    id: 18,
    name: "Last Dash",
    par: 5,
    size: { w: 990, h: 730 },
    start: { x: 170, y: 595 },
    cup: { x: 800, y: 160 },
    fairway: [
      { x: 90, y: 645 },
      { x: 90, y: 430 },
      { x: 260, y: 430 },
      { x: 260, y: 305 },
      { x: 155, y: 305 },
      { x: 155, y: 120 },
      { x: 430, y: 120 },
      { x: 430, y: 245 },
      { x: 570, y: 245 },
      { x: 570, y: 95 },
      { x: 885, y: 95 },
      { x: 885, y: 285 },
      { x: 748, y: 410 },
      { x: 615, y: 645 },
    ],
    obstacles: [
      { kind: "rail", x: 355, y: 360, w: 165, h: 18, angle: 0.5 },
      { kind: "rail", x: 625, y: 410, w: 150, h: 18, angle: -0.48 },
      {
        kind: "triangle",
        points: [
          { x: 540, y: 535 },
          { x: 635, y: 580 },
          { x: 540, y: 625 },
        ],
      },
    ],
    zones: [
      { kind: "fan", x: 268, y: 205, r: 47, angle: Math.PI / 2, force: 0.00043 },
      { kind: "fan", x: 725, y: 205, r: 47, angle: Math.PI, force: 0.00043 },
      { kind: "sand", x: 455, y: 495, r: 50 },
    ],
  },
];

const SPECIAL_BASE_HOLES: HoleDefinition[] = [
  specialHole(
    1001,
    "Sliding Gate",
    1,
    4,
    [
      { kind: "movingRail", x: 512, y: 465, w: 126, h: 18, angle: -0.28, axis: "x", distance: 86, period: 3.4 },
      { kind: "rail", x: 505, y: 218, w: 118, h: 18, angle: 0.28 },
    ],
    [
      { kind: "rotatingBunker", x: 690, y: 360, r: 34, orbitRadius: 54, period: 5.8, count: 2 },
      { kind: "fan", x: 292, y: 555, r: 40, angle: -0.12, force: 0.00034 },
    ],
  ),
  specialHole(
    1002,
    "Orbit Pin",
    0,
    3,
    [
      { kind: "movingRail", x: 405, y: 322, w: 112, h: 18, angle: 0.02, axis: "y", distance: 62, period: 3.1, phase: 0.35 },
      { kind: "triangle", points: [{ x: 620, y: 276 }, { x: 704, y: 306 }, { x: 632, y: 344 }] },
    ],
    [
      { kind: "rotatingBunker", x: 704, y: 216, r: 30, orbitRadius: 42, period: 4.6, phase: 0.4, count: 2 },
      { kind: "fan", x: 392, y: 333, r: 42, angle: -0.35, force: 0.00034 },
    ],
  ),
  specialHole(
    1003,
    "Rail Commute",
    3,
    4,
    [
      { kind: "movingRail", x: 360, y: 390, w: 132, h: 18, angle: -0.35, axis: "x", distance: 74, period: 3.8, phase: 0.2 },
      { kind: "movingRail", x: 617, y: 284, w: 108, h: 18, angle: 0.65, axis: "y", distance: 56, period: 3.2, phase: 0.72 },
    ],
    [
      { kind: "fan", x: 285, y: 505, r: 45, angle: 0, force: 0.00038 },
      { kind: "rotatingBunker", x: 742, y: 414, r: 32, orbitRadius: 48, period: 5.2, phase: 0.2, count: 1 },
    ],
  ),
  specialHole(
    1004,
    "Turntable Bunker",
    2,
    3,
    [
      { kind: "rail", x: 500, y: 265, w: 150, h: 18, angle: 0 },
      { kind: "movingRail", x: 515, y: 495, w: 120, h: 18, angle: 0.45, axis: "y", distance: 64, period: 3.6, phase: 0.5 },
    ],
    [
      { kind: "rotatingBunker", x: 520, y: 548, r: 31, orbitRadius: 58, period: 4.8, phase: 0.1, count: 2 },
      { kind: "fan", x: 385, y: 205, r: 42, angle: Math.PI / 2, force: 0.00036 },
    ],
  ),
  specialHole(
    1005,
    "Pressure Lane",
    5,
    3,
    [
      { kind: "movingRail", x: 515, y: 370, w: 134, h: 18, angle: Math.PI / 2, axis: "x", distance: 38, period: 2.7 },
      { kind: "movingRail", x: 575, y: 370, w: 134, h: 18, angle: Math.PI / 2, axis: "x", distance: 38, period: 2.7, phase: 0.5 },
    ],
    [
      { kind: "fan", x: 250, y: 470, r: 42, angle: 0.25, force: 0.00038 },
      { kind: "rotatingBunker", x: 705, y: 240, r: 28, orbitRadius: 34, period: 3.9, count: 1 },
    ],
  ),
  specialHole(
    1006,
    "Carousel Hook",
    6,
    5,
    [
      { kind: "rail", x: 540, y: 300, w: 165, h: 18, angle: 0.45 },
      { kind: "movingRail", x: 310, y: 278, w: 104, h: 18, angle: 0.12, axis: "y", distance: 58, period: 3.2, phase: 0.25 },
    ],
    [
      { kind: "fan", x: 260, y: 545, r: 42, angle: -0.2, force: 0.00038 },
      { kind: "rotatingBunker", x: 545, y: 535, r: 33, orbitRadius: 62, period: 5.8, phase: 0.6, count: 2 },
      { kind: "fan", x: 675, y: 222, r: 44, angle: Math.PI / 2, force: 0.00032 },
    ],
  ),
  specialHole(
    1007,
    "Rush Hour",
    7,
    4,
    [
      { kind: "movingRail", x: 365, y: 505, w: 130, h: 18, angle: -0.2, axis: "x", distance: 82, period: 3.1, phase: 0.18 },
      { kind: "movingRail", x: 605, y: 325, w: 110, h: 18, angle: -0.62, axis: "y", distance: 52, period: 3.7, phase: 0.66 },
    ],
    [
      { kind: "rotatingBunker", x: 272, y: 505, r: 30, orbitRadius: 44, period: 4.2, count: 2 },
      { kind: "fan", x: 545, y: 210, r: 43, angle: 0.4, force: 0.00036 },
    ],
  ),
  specialHole(
    1008,
    "Sweeper Shelf",
    9,
    4,
    [
      { kind: "movingRail", x: 520, y: 405, w: 150, h: 18, angle: -0.18, axis: "x", distance: 72, period: 3.4, phase: 0.35 },
      { kind: "rail", x: 475, y: 205, w: 125, h: 18, angle: 0.3 },
    ],
    [
      { kind: "rotatingBunker", x: 245, y: 535, r: 30, orbitRadius: 48, period: 4.9, count: 1 },
      { kind: "fan", x: 705, y: 315, r: 42, angle: Math.PI, force: 0.00038 },
    ],
  ),
  specialHole(
    1009,
    "Twin Orbit",
    14,
    5,
    [
      { kind: "movingRail", x: 350, y: 285, w: 132, h: 18, angle: 0.4, axis: "x", distance: 66, period: 3.5, phase: 0.15 },
      { kind: "movingRail", x: 640, y: 455, w: 138, h: 18, angle: -0.48, axis: "y", distance: 72, period: 4.0, phase: 0.58 },
    ],
    [
      { kind: "rotatingBunker", x: 285, y: 205, r: 29, orbitRadius: 46, period: 4.6, count: 1 },
      { kind: "rotatingBunker", x: 720, y: 220, r: 29, orbitRadius: 46, period: 4.6, phase: 0.5, count: 1 },
      { kind: "sand", x: 500, y: 535, r: 42 },
    ],
  ),
  specialHole(
    1010,
    "Final Shuttle",
    17,
    5,
    [
      { kind: "movingRail", x: 355, y: 360, w: 144, h: 18, angle: 0.5, axis: "x", distance: 74, period: 3.1, phase: 0.22 },
      { kind: "movingRail", x: 625, y: 410, w: 130, h: 18, angle: -0.48, axis: "y", distance: 68, period: 3.6, phase: 0.64 },
      { kind: "triangle", points: [{ x: 540, y: 535 }, { x: 635, y: 580 }, { x: 540, y: 625 }] },
    ],
    [
      { kind: "fan", x: 268, y: 205, r: 44, angle: Math.PI / 2, force: 0.00036 },
      { kind: "rotatingBunker", x: 455, y: 495, r: 32, orbitRadius: 56, period: 5.3, phase: 0.3, count: 2 },
    ],
  ),
];

export const HOLES: HoleDefinition[] = sanitizeHoles([...HANDMADE_HOLES, ...makeGeneratedHoles(19, 100)]);
export const SPECIAL_HOLES: HoleDefinition[] = sanitizeHoles(withFunFeatureZones([...SPECIAL_BASE_HOLES, ...makeGeneratedSpecialHoles(1011, 1050)], "special"));
export const LONG_HOLES: HoleDefinition[] = sanitizeHoles(withFunFeatureZones(makeLongSpecialHoles(2001, 2050), "long"));

export function catalogForPack(pack: CoursePack = "classic"): HoleDefinition[] {
  if (pack === "long") return LONG_HOLES;
  return pack === "special" ? SPECIAL_HOLES : HOLES;
}

export function holeFor(index: number, pack: CoursePack = "classic"): HoleDefinition {
  const catalog = catalogForPack(pack);
  return catalog[((index % catalog.length) + catalog.length) % catalog.length];
}

export function holeById(id: number, pack: CoursePack = "classic"): HoleDefinition | undefined {
  return catalogForPack(pack).find((hole) => hole.id === id);
}

export function holeIdsForRound(
  count: number,
  seed: string,
  pack: CoursePack = "classic",
  avoidIds: number[] = [],
  avoidFamilyIds: number[] = [],
): number[] {
  const catalog = catalogForPack(pack);
  const order = shuffledHoleIndexes(seed, pack).map((index) => catalog[index]);
  const avoidIdSet = new Set(avoidIds);
  const avoidFamilySet = new Set(avoidFamilyIds);
  const selected: HoleDefinition[] = [];
  const selectedIds = new Set<number>();
  const target = Math.max(1, Math.round(count));

  const addMatching = (predicate: (hole: HoleDefinition) => boolean): void => {
    for (const hole of order) {
      if (selected.length >= target) return;
      if (selectedIds.has(hole.id) || !predicate(hole)) continue;
      selected.push(hole);
      selectedIds.add(hole.id);
    }
  };

  addMatching((hole) => !avoidIdSet.has(hole.id) && !avoidFamilySet.has(holeFamilyId(hole)));
  addMatching((hole) => !avoidIdSet.has(hole.id));
  addMatching(() => true);

  return selected.map((hole) => hole.id);
}

export function holesForRoundIds(ids: number[], pack: CoursePack = "classic"): HoleDefinition[] {
  const catalog = catalogForPack(pack);
  return ids.map((id) => catalog.find((hole) => hole.id === id)).filter((hole): hole is HoleDefinition => Boolean(hole));
}

export function holesForRound(count: number, seed: string, pack: CoursePack = "classic"): HoleDefinition[] {
  return holesForRoundIds(holeIdsForRound(count, seed, pack), pack);
}

export function holeForRound(index: number, seed: string, pack: CoursePack = "classic"): HoleDefinition {
  const order = shuffledHoleIndexes(seed, pack);
  return holeFor(order[((index % order.length) + order.length) % order.length], pack);
}

export function totalPar(count: number, pack: CoursePack = "classic"): number {
  return catalogForPack(pack)
    .slice(0, count)
    .reduce((sum, hole) => sum + hole.par, 0);
}

export function totalParForRound(count: number, seed: string, pack: CoursePack = "classic"): number {
  return holesForRound(count, seed, pack).reduce((sum, hole) => sum + hole.par, 0);
}

export function holeFamilyId(hole: HoleDefinition): number {
  return hole.familyId ?? hole.id;
}

function specialHole(
  id: number,
  name: string,
  baseIndex: number,
  par: number,
  obstacles: CourseObstacle[],
  zones: DiscZone[],
): HoleDefinition {
  const base = HANDMADE_HOLES[baseIndex];
  return {
    id,
    familyId: id,
    name,
    par,
    size: { ...base.size },
    start: { ...base.start },
    cup: { ...base.cup },
    fairway: base.fairway.map((point) => ({ ...point })),
    obstacles,
    zones,
  };
}

function withFunFeatureZones(holes: HoleDefinition[], pack: "special" | "long"): HoleDefinition[] {
  return holes.map((hole, index) => ({
    ...hole,
    zones: [...hole.zones, ...funFeatureZonesForHole(hole, index, pack)],
  }));
}

function funFeatureZonesForHole(hole: HoleDefinition, index: number, pack: "special" | "long"): DiscZone[] {
  const zones: DiscZone[] = [];
  const long = pack === "long";
  const route = routePathForHole(hole, long ? 92 : 54);
  const lateralSign = index % 2 === 0 ? 1 : -1;
  const theme = index % (long ? 5 : 4);

  const addJump = (amount: number, radius: number, boost: number, lateral = 0): void => {
    const point = placeFeatureOnRoute(hole, route, amount, lateral, radius);
    zones.push({
      kind: "jump",
      x: point.x,
      y: point.y,
      r: radius,
      angle: point.angle,
      boost,
    });
  };

  const addBumper = (amount: number, radius: number, strength: number, lateral: number): void => {
    const point = placeFeatureOnRoute(hole, route, amount, lateral, radius);
    zones.push({
      kind: "bumper",
      x: point.x,
      y: point.y,
      r: radius,
      angle: point.angle + Math.PI / 2,
      strength,
    });
  };

  const addPortalPair = (entryAmount: number, exitAmount: number, radius: number, boost: number): void => {
    const entry = placeFeatureOnRoute(hole, route, entryAmount, 0, radius);
    const exit = placeFeatureOnRoute(hole, route, exitAmount, 0, radius);
    const group = `${pack}-${hole.id}-warp`;
    zones.push(
      {
        kind: "portal",
        x: entry.x,
        y: entry.y,
        r: radius,
        angle: entry.angle,
        group,
        exit: 0,
        boost,
      },
      {
        kind: "portal",
        x: exit.x,
        y: exit.y,
        r: radius,
        angle: exit.angle,
        group,
        exit: 1,
        boost,
      },
    );
  };

  if (long) {
    if (theme === 0) {
      addJump(0.24, 54, 5.8);
      addJump(0.58, 48, 5.1);
    } else if (theme === 1) {
      addPortalPair(0.28, 0.72, 46, 1.24);
    } else if (theme === 2) {
      addBumper(0.46, 46, 7.4, lateralSign * 36);
      addJump(0.7, 48, 5.2);
    } else if (theme === 3) {
      addJump(0.36, 56, 6.0);
    } else {
      addPortalPair(0.24, 0.58, 44, 1.22);
      addBumper(0.78, 44, 7.2, -lateralSign * 34);
    }
  } else if (theme === 0) {
    addJump(0.36, 34, 4.2);
    addBumper(0.58, 31, 5.8, lateralSign * 24);
  } else if (theme === 1) {
    addPortalPair(0.28, 0.7, 30, 1.34);
  } else if (theme === 2) {
    addJump(0.48, 36, 4.4);
  } else {
    addBumper(0.5, 31, 5.9, -lateralSign * 24);
    addJump(0.74, 32, 4.1);
  }

  return zones;
}

function placeFeatureOnRoute(hole: HoleDefinition, route: Point[], amount: number, lateral: number, radius: number): Point & { angle: number } {
  const routePoint = pointAlongPath(route, amount);
  const normal = { x: -Math.sin(routePoint.angle), y: Math.cos(routePoint.angle) };
  const centerline = { x: routePoint.x, y: routePoint.y };
  const preferred = {
    x: routePoint.x + normal.x * lateral,
    y: routePoint.y + normal.y * lateral,
  };
  const anchor = interiorPoint(hole.fairway);
  const candidates = [preferred, centerline];

  for (const candidate of candidates) {
    for (const target of [centerline, anchor]) {
      for (let step = 0; step <= 18; step += 1) {
        const point = lerpPoint(candidate, target, step / 18);
        if (circleInsidePolygon({ x: point.x, y: point.y, r: radius }, hole.fairway)) {
          return { x: Math.round(point.x), y: Math.round(point.y), angle: routePoint.angle };
        }
      }
    }
  }

  return { x: Math.round(anchor.x), y: Math.round(anchor.y), angle: routePoint.angle };
}

function routePathForHole(hole: HoleDefinition, preferredStep: number): Point[] {
  for (const step of [preferredStep, preferredStep * 0.72, preferredStep * 0.52]) {
    const path = findRoutePath(hole, step);
    if (path.length > 1) return path;
  }
  return [hole.start, hole.cup];
}

function findRoutePath(hole: HoleDefinition, rawStep: number): Point[] {
  const bounds = polygonBounds(hole.fairway);
  const step = Math.max(32, Math.round(rawStep));
  const minX = bounds.minX - step;
  const minY = bounds.minY - step;
  const columns = Math.ceil((bounds.maxX - bounds.minX + step * 2) / step) + 1;
  const rows = Math.ceil((bounds.maxY - bounds.minY + step * 2) / step) + 1;
  const points: Point[] = [];
  const valid: boolean[] = [];

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const point = {
        x: minX + column * step,
        y: minY + row * step,
      };
      points.push(point);
      valid.push(pointInPolygon(point, hole.fairway));
    }
  }

  const start = nearestValidRouteIndex(hole.start, points, valid);
  const goal = nearestValidRouteIndex(hole.cup, points, valid);
  if (start < 0 || goal < 0) return [];

  const gScore = Array(points.length).fill(Number.POSITIVE_INFINITY);
  const fScore = Array(points.length).fill(Number.POSITIVE_INFINITY);
  const cameFrom = Array(points.length).fill(-1);
  const open = new Set<number>([start]);
  gScore[start] = 0;
  fScore[start] = distance(points[start], points[goal]);

  while (open.size > 0) {
    let current = -1;
    let best = Number.POSITIVE_INFINITY;
    for (const candidate of open) {
      if (fScore[candidate] < best) {
        current = candidate;
        best = fScore[candidate];
      }
    }

    if (current === goal) {
      return [hole.start, ...reconstructRoute(cameFrom, current, points), hole.cup];
    }

    open.delete(current);
    const currentColumn = current % columns;
    const currentRow = Math.floor(current / columns);
    for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
      for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
        if (rowOffset === 0 && columnOffset === 0) continue;
        const nextColumn = currentColumn + columnOffset;
        const nextRow = currentRow + rowOffset;
        if (nextColumn < 0 || nextColumn >= columns || nextRow < 0 || nextRow >= rows) continue;
        const next = nextRow * columns + nextColumn;
        if (!valid[next] || !segmentInsidePolygon(points[current], points[next], hole.fairway)) continue;
        const moveCost = Math.hypot(columnOffset, rowOffset) * step;
        const tentative = gScore[current] + moveCost;
        if (tentative >= gScore[next]) continue;
        cameFrom[next] = current;
        gScore[next] = tentative;
        fScore[next] = tentative + distance(points[next], points[goal]);
        open.add(next);
      }
    }
  }

  return [];
}

function nearestValidRouteIndex(target: Point, points: Point[], valid: boolean[]): number {
  let bestIndex = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < points.length; index += 1) {
    if (!valid[index]) continue;
    const candidateDistance = distance(target, points[index]);
    if (candidateDistance < bestDistance) {
      bestDistance = candidateDistance;
      bestIndex = index;
    }
  }
  return bestIndex;
}

function reconstructRoute(cameFrom: number[], current: number, points: Point[]): Point[] {
  const route = [points[current]];
  let next = cameFrom[current];
  while (next >= 0) {
    route.push(points[next]);
    next = cameFrom[next];
  }
  return route.reverse();
}

function segmentInsidePolygon(from: Point, to: Point, polygon: Point[]): boolean {
  for (let index = 1; index <= 6; index += 1) {
    const point = lerpPoint(from, to, index / 7);
    if (!pointInPolygon(point, polygon)) return false;
  }
  return true;
}

function pointAlongPath(points: Point[], amount: number): Point & { angle: number } {
  if (points.length < 2) {
    return { x: points[0]?.x ?? 0, y: points[0]?.y ?? 0, angle: 0 };
  }
  const lengths = [];
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const length = distance(points[index - 1], points[index]);
    lengths.push(length);
    total += length;
  }
  let target = total * Math.min(0.94, Math.max(0.06, amount));
  for (let index = 1; index < points.length; index += 1) {
    const length = lengths[index - 1];
    if (target <= length || index === points.length - 1) {
      const from = points[index - 1];
      const to = points[index];
      const mix = length > 0 ? target / length : 0;
      return {
        ...lerpPoint(from, to, mix),
        angle: Math.atan2(to.y - from.y, to.x - from.x),
      };
    }
    target -= length;
  }
  const from = points[points.length - 2];
  const to = points[points.length - 1];
  return {
    x: to.x,
    y: to.y,
    angle: Math.atan2(to.y - from.y, to.x - from.x),
  };
}

function polygonBounds(points: Point[]): { minX: number; minY: number; maxX: number; maxY: number } {
  return points.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point.x),
      minY: Math.min(bounds.minY, point.y),
      maxX: Math.max(bounds.maxX, point.x),
      maxY: Math.max(bounds.maxY, point.y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
}

function makeGeneratedHoles(firstId: number, lastId: number): HoleDefinition[] {
  const names = [
    "Cedar Run",
    "Bank Shot",
    "Long Drift",
    "Pocket Bend",
    "Double Shelf",
    "Sidewind",
    "Pin Chase",
    "Green Hook",
    "Rail Garden",
    "Final Curl",
  ];
  const holes: HoleDefinition[] = [];
  for (let id = firstId; id <= lastId; id += 1) {
    const base = HANDMADE_HOLES[(id * 7) % HANDMADE_HOLES.length];
    const variant = id - firstId;
    const long = variant % 5 === 0;
    const scaleX = 0.94 + ((variant * 37) % 32) / 100 + (long ? 0.12 : 0);
    const scaleY = 0.96 + ((variant * 23) % 24) / 100 + (long ? 0.08 : 0);
    const mirrorX = variant % 3 === 1;
    const margin = 36 + (variant % 4) * 4;
    const size = {
      w: Math.round(base.size.w * scaleX + margin * 2),
      h: Math.round(base.size.h * scaleY + margin * 2),
    };
    const transformPoint = (point: Point): Point => ({
      x: Math.round(margin + (mirrorX ? base.size.w - point.x : point.x) * scaleX),
      y: Math.round(margin + point.y * scaleY),
    });
    const transformAngle = (angle = 0): number => (mirrorX ? Math.PI - angle : angle);
    const fairway = base.fairway.map(transformPoint);
    const start = transformPoint(base.start);
    const cup = transformPoint(base.cup);
    const obstacles = base.obstacles.map((obstacle): CourseObstacle => {
      if (obstacle.kind === "rail") {
        const point = transformPoint(obstacle);
        return {
          kind: "rail" as const,
          x: point.x,
          y: point.y,
          w: Math.round(obstacle.w * scaleX),
          h: Math.max(16, Math.round(obstacle.h * Math.min(scaleX, scaleY))),
          angle: transformAngle(obstacle.angle),
        };
      }
      if (obstacle.kind === "triangle") {
        return {
          kind: "triangle",
          points: obstacle.points.map(transformPoint) as [Point, Point, Point],
        };
      }
      return {
        kind: "rail",
        x: transformPoint(obstacle).x,
        y: transformPoint(obstacle).y,
        w: Math.round(obstacle.w * scaleX),
        h: Math.max(16, Math.round(obstacle.h * Math.min(scaleX, scaleY))),
        angle: transformAngle(obstacle.angle),
      };
    });
    const zones = base.zones.map((zone, zoneIndex) => {
      const point = transformPoint(zone);
      return {
        ...zone,
        x: point.x,
        y: point.y,
        r: Math.round(zone.r * (0.9 + ((variant + zoneIndex) % 5) * 0.04)),
        angle: transformAngle(zone.angle),
      };
    });

    holes.push({
      id,
      familyId: base.id,
      name: `${names[variant % names.length]} ${Math.floor(variant / names.length) + 1}`,
      par: long ? Math.min(5, base.par + 1) : base.par,
      size,
      start,
      cup,
      fairway,
      obstacles,
      zones,
    });
  }
  return holes;
}

function makeGeneratedSpecialHoles(firstId: number, lastId: number): HoleDefinition[] {
  const names = [
    "Slider",
    "Orbit",
    "Shuttle",
    "Carousel",
    "Timing Gate",
    "Sweeper",
    "Clockwork",
    "Spiral",
    "Metro",
    "Pinwheel",
  ];
  const holes: HoleDefinition[] = [];
  for (let id = firstId; id <= lastId; id += 1) {
    const base = SPECIAL_BASE_HOLES[(id - firstId) % SPECIAL_BASE_HOLES.length];
    const variant = id - firstId;
    const long = variant % 6 === 0;
    const scaleX = 0.96 + ((variant * 29) % 30) / 100 + (long ? 0.12 : 0);
    const scaleY = 0.96 + ((variant * 17) % 25) / 100 + (long ? 0.08 : 0);
    const mirrorX = variant % 4 === 1;
    const margin = 32 + (variant % 5) * 4;
    const size = {
      w: Math.round(base.size.w * scaleX + margin * 2),
      h: Math.round(base.size.h * scaleY + margin * 2),
    };
    const transformPoint = (point: Point): Point => ({
      x: Math.round(margin + (mirrorX ? base.size.w - point.x : point.x) * scaleX),
      y: Math.round(margin + point.y * scaleY),
    });
    const transformAngle = (angle = 0): number => (mirrorX ? Math.PI - angle : angle);
    const fairway = base.fairway.map(transformPoint);
    const start = transformPoint(base.start);
    const cup = transformPoint(base.cup);
    const obstacles = base.obstacles.map((obstacle, obstacleIndex): CourseObstacle => {
      if (obstacle.kind === "triangle") {
        return {
          kind: "triangle",
          points: obstacle.points.map(transformPoint) as [Point, Point, Point],
        };
      }

      const point = transformPoint(obstacle);
      const baseRail = {
        x: point.x,
        y: point.y,
        w: Math.round(obstacle.w * scaleX),
        h: Math.max(16, Math.round(obstacle.h * Math.min(scaleX, scaleY))),
        angle: transformAngle(obstacle.angle),
      };
      if (obstacle.kind === "rail") {
        return { kind: "rail", ...baseRail };
      }
      const axisScale = obstacle.axis === "x" ? scaleX : scaleY;
      return {
        kind: "movingRail",
        ...baseRail,
        axis: obstacle.axis,
        distance: Math.round(obstacle.distance * axisScale * (0.82 + ((variant + obstacleIndex) % 5) * 0.08)),
        period: Math.max(2.4, obstacle.period * (0.88 + ((variant + obstacleIndex) % 6) * 0.06)),
        phase: (obstacle.phase ?? 0) + ((variant + obstacleIndex) % 7) * 0.11,
      };
    });
    const zones = base.zones.map((zone, zoneIndex): DiscZone => {
      const point = transformPoint(zone);
      const radiusScale = 0.88 + ((variant + zoneIndex) % 5) * 0.04;
      const baseZone: DiscZone = {
        ...zone,
        x: point.x,
        y: point.y,
        r: Math.round(zone.r * radiusScale),
        angle: transformAngle(zone.angle),
      };
      if (zone.kind !== "rotatingBunker") return baseZone;
      return {
        ...baseZone,
        orbitRadius: Math.round((zone.orbitRadius ?? 42) * Math.min(scaleX, scaleY) * (0.82 + ((variant + zoneIndex) % 4) * 0.08)),
        period: Math.max(3.2, (zone.period ?? 5) * (0.9 + ((variant + zoneIndex) % 5) * 0.05)),
        phase: (zone.phase ?? 0) + ((variant + zoneIndex) % 8) * 0.09,
      };
    });

    holes.push({
      id,
      familyId: base.id,
      name: `${names[variant % names.length]} ${Math.floor(variant / names.length) + 1}`,
      par: long ? Math.min(5, base.par + 1) : base.par,
      size,
      start,
      cup,
      fairway,
      obstacles,
      zones,
    });
  }
  return holes;
}

function makeLongSpecialHoles(firstId: number, lastId: number): HoleDefinition[] {
  const names = [
    "Long Haul",
    "Marathon Gate",
    "Runway Orbit",
    "Expedition",
    "Endurance Rail",
    "Cross-Country",
    "Big Loop",
    "Giant Shuttle",
    "Mega Metro",
    "Overland",
  ];
  const holes: HoleDefinition[] = [];
  for (let id = firstId; id <= lastId; id += 1) {
    const base = SPECIAL_BASE_HOLES[(id - firstId) % SPECIAL_BASE_HOLES.length];
    const variant = id - firstId;
    const lengthScale = 3 + ((variant * 17) % 21) / 10;
    const widthScale = 1.55 + ((variant * 11) % 10) / 10;
    const mirrorX = variant % 4 === 1;
    const margin = 96 + (variant % 5) * 18;
    const size = {
      w: Math.round(base.size.w * lengthScale + margin * 2),
      h: Math.round(base.size.h * widthScale + margin * 2),
    };
    const transformPoint = (point: Point): Point => ({
      x: Math.round(margin + (mirrorX ? base.size.w - point.x : point.x) * lengthScale),
      y: Math.round(margin + point.y * widthScale),
    });
    const transformAngle = (angle = 0): number => (mirrorX ? Math.PI - angle : angle);
    const fairway = base.fairway.map(transformPoint);
    const start = transformPoint(base.start);
    const cup = transformPoint(base.cup);
    const obstacles: CourseObstacle[] = base.obstacles.map((obstacle, obstacleIndex): CourseObstacle => {
      if (obstacle.kind === "triangle") {
        return {
          kind: "triangle",
          points: obstacle.points.map(transformPoint) as [Point, Point, Point],
        };
      }

      const point = transformPoint(obstacle);
      const railScale = 1.35 + ((variant + obstacleIndex) % 5) * 0.12;
      const baseRail = {
        x: point.x,
        y: point.y,
        w: Math.round(obstacle.w * railScale),
        h: Math.max(18, Math.round(obstacle.h * 1.18)),
        angle: transformAngle(obstacle.angle),
      };
      if (obstacle.kind === "rail") {
        return {
          kind: "movingRail",
          ...baseRail,
          axis: obstacleIndex % 2 === 0 ? "x" : "y",
          distance: Math.round(70 + lengthScale * 22 + obstacleIndex * 18),
          period: 3.4 + ((variant + obstacleIndex) % 5) * 0.35,
          phase: ((variant + obstacleIndex) % 7) * 0.1,
        };
      }
      return {
        kind: "movingRail",
        ...baseRail,
        axis: obstacle.axis,
        distance: Math.round(obstacle.distance * (obstacle.axis === "x" ? lengthScale : widthScale) * 0.72),
        period: obstacle.period * (1.15 + ((variant + obstacleIndex) % 4) * 0.08),
        phase: (obstacle.phase ?? 0) + ((variant + obstacleIndex) % 7) * 0.08,
      };
    });

    const zones = base.zones.map((zone, zoneIndex): DiscZone => {
      const point = transformPoint(zone);
      const radiusScale = 1.08 + ((variant + zoneIndex) % 4) * 0.08;
      const baseZone: DiscZone = {
        ...zone,
        x: point.x,
        y: point.y,
        r: Math.round(zone.r * radiusScale),
        angle: transformAngle(zone.angle),
        force: zone.force ? zone.force * 0.88 : zone.force,
      };
      if (zone.kind !== "rotatingBunker") return baseZone;
      return {
        ...baseZone,
        orbitRadius: Math.round((zone.orbitRadius ?? 46) * (1.3 + ((variant + zoneIndex) % 4) * 0.13)),
        period: Math.max(4.2, (zone.period ?? 5) * (1.05 + ((variant + zoneIndex) % 5) * 0.06)),
        phase: (zone.phase ?? 0) + ((variant + zoneIndex) % 8) * 0.07,
      };
    });

    const extraRailCount = 2 + (variant % 3);
    for (let extra = 0; extra < extraRailCount; extra += 1) {
      const amount = (extra + 1) / (extraRailCount + 1);
      const center = lerpPoint(start, cup, amount);
      obstacles.push({
        kind: "movingRail",
        x: Math.round(center.x + Math.sin((variant + extra) * 1.7) * 110 * lengthScale * 0.18),
        y: Math.round(center.y + Math.cos((variant + extra) * 1.3) * 72 * widthScale * 0.22),
        w: Math.round(138 + ((variant + extra) % 4) * 24),
        h: 20,
        angle: transformAngle((extra % 2 === 0 ? 0.32 : -0.48) + (variant % 5) * 0.05),
        axis: extra % 2 === 0 ? "x" : "y",
        distance: Math.round(92 + lengthScale * 18 + extra * 16),
        period: 3.4 + ((variant + extra) % 6) * 0.28,
        phase: ((variant + extra) % 8) * 0.09,
      });
      zones.push({
        kind: "rotatingBunker",
        x: Math.round(center.x + Math.cos((variant + extra) * 1.1) * 85 * lengthScale * 0.16),
        y: Math.round(center.y + Math.sin((variant + extra) * 1.4) * 70 * widthScale * 0.2),
        r: 34 + ((variant + extra) % 4) * 3,
        orbitRadius: 58 + ((variant + extra) % 5) * 7,
        period: 5.2 + ((variant + extra) % 4) * 0.42,
        phase: ((variant + extra) % 9) * 0.08,
        count: extra % 2 === 0 ? 2 : 1,
      });
    }

    holes.push({
      id,
      familyId: base.id,
      name: `${names[variant % names.length]} ${Math.floor(variant / names.length) + 1}`,
      par: Math.max(6, base.par + Math.round(lengthScale)),
      size,
      start,
      cup,
      fairway,
      obstacles,
      zones,
    });
  }
  return holes;
}

function sanitizeHoles(holes: HoleDefinition[]): HoleDefinition[] {
  return holes.map((hole) => {
    const fairway = hole.fairway;
    return {
      ...hole,
      zones: hole.zones
        .map((zone) => fitZoneInsideFairway(zone, fairway))
        .filter((zone): zone is DiscZone => Boolean(zone)),
      obstacles: hole.obstacles
        .map((obstacle) => fitObstacleInsideFairway(obstacle, fairway))
        .filter((obstacle): obstacle is CourseObstacle => Boolean(obstacle)),
    };
  });
}

function fitZoneInsideFairway(zone: DiscZone, fairway: Point[]): DiscZone | undefined {
  if (zoneInsideFairway(zone, fairway)) return zone;

  const anchor = interiorPoint(fairway);
  for (let moveStep = 1; moveStep <= 18; moveStep += 1) {
    const move = moveStep / 18;
    const center = lerpPoint(zone, anchor, move);
    for (let shrinkStep = 0; shrinkStep <= 10; shrinkStep += 1) {
      const shrink = Math.max(0.42, 1 - move * 0.2 - shrinkStep * 0.055);
      const r = Math.round(Math.max(24, zone.r * shrink));
      const orbitRadius = zone.kind === "rotatingBunker" ? Math.round(Math.max(20, (zone.orbitRadius ?? 42) * shrink)) : zone.orbitRadius;
      const candidate = { ...zone, x: Math.round(center.x), y: Math.round(center.y), r, orbitRadius };
      if (zoneInsideFairway(candidate, fairway)) return candidate;
    }
  }

  return undefined;
}

function fitObstacleInsideFairway(
  obstacle: CourseObstacle,
  fairway: Point[],
): CourseObstacle | undefined {
  if (obstacleInsideFairway(obstacle, fairway)) return obstacle;

  if (obstacle.kind === "rail" || obstacle.kind === "movingRail") {
    const anchor = interiorPoint(fairway);
    for (let moveStep = 1; moveStep <= 12; moveStep += 1) {
      const move = moveStep / 12;
      const center = lerpPoint(obstacle, anchor, move);
      for (let shrinkStep = 0; shrinkStep <= 7; shrinkStep += 1) {
        const widthScale = 1 - shrinkStep * 0.08;
        const candidate: CourseObstacle =
          obstacle.kind === "movingRail"
            ? {
                ...obstacle,
                x: Math.round(center.x),
                y: Math.round(center.y),
                w: Math.round(Math.max(64, obstacle.w * widthScale)),
                distance: Math.round(Math.max(18, obstacle.distance * widthScale)),
              }
            : {
                ...obstacle,
                x: Math.round(center.x),
                y: Math.round(center.y),
                w: Math.round(Math.max(64, obstacle.w * widthScale)),
              };
        if (obstacleInsideFairway(candidate, fairway)) return candidate;
      }
    }
    return undefined;
  }

  const center = triangleCenter(obstacle.points);
  const anchor = interiorPoint(fairway);
  for (let moveStep = 1; moveStep <= 12; moveStep += 1) {
    const move = moveStep / 12;
    const nextCenter = lerpPoint(center, anchor, move);
    const dx = nextCenter.x - center.x;
    const dy = nextCenter.y - center.y;
    const candidate: TriangleObstacle = {
      kind: "triangle",
      points: obstacle.points.map((point) => ({ x: Math.round(point.x + dx), y: Math.round(point.y + dy) })) as [Point, Point, Point],
    };
    if (obstacleInsideFairway(candidate, fairway)) return candidate;
  }

  return undefined;
}

function zoneInsideFairway(zone: DiscZone, fairway: Point[]): boolean {
  if (zone.kind !== "rotatingBunker") return circleInsidePolygon(zone, fairway);
  const period = Math.max(0.6, zone.period ?? 5);
  for (let sample = 0; sample < 16; sample += 1) {
    const seconds = (period * sample) / 16;
    if (!rotatingBunkerCircles(zone, seconds).every((circle) => circleInsidePolygon(circle, fairway))) {
      return false;
    }
  }
  return true;
}

export function movingRailCenter(obstacle: MovingRailObstacle, seconds: number): Point {
  const period = Math.max(0.6, obstacle.period);
  const phase = (obstacle.phase ?? 0) * Math.PI * 2;
  const offset = Math.sin((seconds / period) * Math.PI * 2 + phase) * obstacle.distance;
  return {
    x: obstacle.x + (obstacle.axis === "x" ? offset : 0),
    y: obstacle.y + (obstacle.axis === "y" ? offset : 0),
  };
}

export function rotatingBunkerCircles(zone: DiscZone, seconds = 0): Array<{ x: number; y: number; r: number }> {
  if (zone.kind !== "rotatingBunker") return [{ x: zone.x, y: zone.y, r: zone.r }];
  const count = Math.max(1, Math.min(3, Math.round(zone.count ?? 1)));
  const period = Math.max(0.6, zone.period ?? 5);
  const orbitRadius = zone.orbitRadius ?? 42;
  const start = (seconds / period) * Math.PI * 2 + (zone.phase ?? 0) * Math.PI * 2;
  return Array.from({ length: count }, (_, index) => {
    const angle = start + (Math.PI * 2 * index) / count;
    return {
      x: zone.x + Math.cos(angle) * orbitRadius,
      y: zone.y + Math.sin(angle) * orbitRadius,
      r: zone.r,
    };
  });
}

function circleInsidePolygon(circle: { x: number; y: number; r: number }, polygon: Point[]): boolean {
  if (!pointInPolygon(circle, polygon)) return false;
  for (let index = 0; index < polygon.length; index += 1) {
    const from = polygon[index];
    const to = polygon[(index + 1) % polygon.length];
    if (distancePointToSegment(circle, from, to) < circle.r + 0.5) {
      return false;
    }
  }
  return true;
}

function distancePointToSegment(point: Point, from: Point, to: Point): number {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return distance(point, from);
  const t = Math.max(0, Math.min(1, ((point.x - from.x) * dx + (point.y - from.y) * dy) / lengthSquared));
  return distance(point, { x: from.x + dx * t, y: from.y + dy * t });
}

function obstacleInsideFairway(obstacle: CourseObstacle, fairway: Point[]): boolean {
  if (obstacle.kind === "triangle") {
    return obstacle.points.every((point) => pointInPolygon(point, fairway));
  }

  if (obstacle.kind === "movingRail") {
    return [-1, -0.5, 0, 0.5, 1].every((position) => {
      const center = {
        x: obstacle.x + (obstacle.axis === "x" ? obstacle.distance * position : 0),
        y: obstacle.y + (obstacle.axis === "y" ? obstacle.distance * position : 0),
      };
      return railCorners({ ...obstacle, x: center.x, y: center.y }).every((point) => pointInPolygon(point, fairway));
    });
  }

  return railCorners(obstacle).every((point) => pointInPolygon(point, fairway));
}

function railCorners(obstacle: { x: number; y: number; w: number; h: number; angle?: number }): Point[] {
  const angle = obstacle.angle ?? 0;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const halfW = obstacle.w / 2;
  const halfH = obstacle.h / 2;
  return [
    { x: -halfW, y: -halfH },
    { x: halfW, y: -halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: halfH },
  ].map((point) => ({
    x: obstacle.x + point.x * cos - point.y * sin,
    y: obstacle.y + point.x * sin + point.y * cos,
  }));
}

function interiorPoint(points: Point[]): Point {
  const average = {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
  if (pointInPolygon(average, points)) return average;

  let best = points[0];
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const point of points) {
    const distance = Math.hypot(point.x - average.x, point.y - average.y);
    if (distance < bestDistance) {
      best = point;
      bestDistance = distance;
    }
  }
  return best;
}

function lerpPoint(from: Point, to: Point, amount: number): Point {
  return {
    x: from.x + (to.x - from.x) * amount,
    y: from.y + (to.y - from.y) * amount,
  };
}

function distance(from: Point, to: Point): number {
  return Math.hypot(from.x - to.x, from.y - to.y);
}

function triangleCenter(points: [Point, Point, Point]): Point {
  return {
    x: (points[0].x + points[1].x + points[2].x) / 3,
    y: (points[0].y + points[1].y + points[2].y) / 3,
  };
}

function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersects = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function shuffledHoleIndexes(seed: string, pack: CoursePack): number[] {
  const random = mulberry32(hashSeed(seed || "pigeon-putt"));
  const families = new Map<number, number[]>();
  const catalog = catalogForPack(pack);

  catalog.forEach((hole, index) => {
    const family = holeFamilyId(hole);
    const indexes = families.get(family) ?? [];
    indexes.push(index);
    families.set(family, indexes);
  });

  const familyOrder = shuffle([...families.keys()], random);
  for (const indexes of families.values()) {
    shuffle(indexes, random);
  }

  const order: number[] = [];
  let madeProgress = true;
  while (madeProgress && order.length < catalog.length) {
    madeProgress = false;
    for (const family of familyOrder) {
      const indexes = families.get(family);
      const next = indexes?.shift();
      if (next === undefined) continue;
      order.push(next);
      madeProgress = true;
    }
  }

  return order;
}

function shuffle<T>(items: T[], random: () => number): T[] {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
  }
  return items;
}

function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  return () => {
    let value = (seed += 0x6d2b79f5);
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}
